// Pure geometry helpers — no DOM, no state mutation. Components call
// these to derive values for reactive props.

function itemRect(it) {
  return { x: it.x || 0, y: it.y || 0, w: it.w || 0, h: it.h || 0 }
}

// Generic bbox over an array of rect-like objects {x,y,w,h}.
export function bboxOfRects(rects) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const r of rects) {
    if (!r) continue
    if (r.x < minX) minX = r.x
    if (r.y < minY) minY = r.y
    if (r.x + r.w > maxX) maxX = r.x + r.w
    if (r.y + r.h > maxY) maxY = r.y + r.h
  }
  if (!Number.isFinite(minX)) return null
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
}

function bboxOfItems(items, ids) {
  return bboxOfRects(ids.map((id) => items[id] && itemRect(items[id])))
}

export function bboxForSelection() {
  const v2 = this.call('getCanvasV2'); if (!v2) return null
  return bboxOfItems(v2.items, v2.selection.ids)
}

export function bboxForGroup(groupId) {
  const v2 = this.call('getCanvasV2')
  const g = v2?.groups?.[groupId]; if (!g) return null
  return bboxOfItems(v2.items, g.children)
}

export function itemsInMarqueeRect(m) {
  const v2 = this.call('getCanvasV2')
  if (!v2 || !m) return []
  const mx2 = m.x + m.w, my2 = m.y + m.h
  const hits = []
  for (const [id, it] of Object.entries(v2.items)) {
    const r = itemRect(it)
    const intersects = !(r.x + r.w < m.x || r.x > mx2 || r.y + r.h < m.y || r.y > my2)
    if (intersects) hits.push(id)
  }
  return hits
}

// Returns { dx, dy, lines } where lines is a list of active snap guides
// expressed in canvas-space. Called with { rect, movingIds }.
export function snapRect(opts) {
  const v2 = this.call('getCanvasV2')
  if (!v2 || !v2.snap.enabled) return { dx: 0, dy: 0, lines: [] }
  const { rect, movingIds = [] } = opts || {}
  if (!rect) return { dx: 0, dy: 0, lines: [] }

  const threshold = (v2.snap.threshold || 6) / (v2.viewport.scale || 1)
  const lines = []
  let dx = 0, dy = 0
  let bestX = threshold, bestY = threshold

  const rxs = [rect.x, rect.x + rect.w / 2, rect.x + rect.w]
  const rys = [rect.y, rect.y + rect.h / 2, rect.y + rect.h]

  const considerX = (target, source, kind) => {
    const d = target - source
    if (Math.abs(d) <= bestX) {
      bestX = Math.abs(d); dx = d
      lines.push({ axis: 'v', pos: target, kind })
    }
  }
  const considerY = (target, source, kind) => {
    const d = target - source
    if (Math.abs(d) <= bestY) {
      bestY = Math.abs(d); dy = d
      lines.push({ axis: 'h', pos: target, kind })
    }
  }

  if (v2.snap.toItems) {
    const moving = new Set(movingIds)
    for (const [id, it] of Object.entries(v2.items)) {
      if (moving.has(id)) continue
      const txs = [it.x, it.x + it.w / 2, it.x + it.w]
      for (const t of txs) for (const s of rxs) considerX(t, s, 'item')
      const tys = [it.y, it.y + it.h / 2, it.y + it.h]
      for (const t of tys) for (const s of rys) considerY(t, s, 'item')
    }
  }

  if (v2.snap.toGuides) {
    for (const gx of v2.guides.vertical || []) for (const s of rxs) considerX(gx, s, 'guide')
    for (const gy of v2.guides.horizontal || []) for (const s of rys) considerY(gy, s, 'guide')
  }

  if (v2.snap.toGrid) {
    const step = v2.snap.gridStep || 64
    for (const s of rxs) {
      const t = Math.round(s / step) * step
      considerX(t, s, 'grid')
    }
    for (const s of rys) {
      const t = Math.round(s / step) * step
      considerY(t, s, 'grid')
    }
  }

  return { dx, dy, lines }
}
